import React from "react";
import styled from "styled-components";
import { colors, typography } from "../styles/designSystem";
import { SubsectionHeadline } from "../components/Typography";

import PromptPage from "../pngs/Prompt_Page.png";
import ViewMeal from "../pngs/View_Meal.png";
import IngrediantsList from "../pngs/Ingrediants_list.png";
import AddToCart from "../pngs/Add_to_cart.png";
import SavedMeals from "../pngs/Saved_Meals.png";

const PageWrapper = styled.div`
    max-width: 900px;
    margin: 0 auto;
    padding: 2rem 1.5rem 6rem;
`;

const HeroSection = styled.section`
    text-align: center;
    padding: 3rem 0 3rem;
    border-bottom: 1px solid ${({ theme }) => theme.borderLight};
    margin-bottom: 4rem;
`;

const HeroLabel = styled.span`
    display: inline-block;
    font-size: ${typography.fontSize[100]};
    font-weight: ${typography.fontWeight.semibold};
    text-transform: uppercase;
    letter-spacing: 0.12em;
    color: ${colors.emerald[500]};
    background: ${colors.emerald[500]}15;
    padding: 6px 16px;
    border-radius: 999px;
    margin-bottom: 1.5rem;
`;

const HeroTitle = styled.h1`
    font-size: ${typography.fontSize[900]};
    font-weight: ${typography.fontWeight.bold};
    color: ${({ theme }) => theme.text};
    line-height: ${typography.lineHeight[200]};
    margin: 0 0 1.5rem;

    @media (max-width: 768px) {
        font-size: ${typography.fontSize[700]};
    }
`;

const HeroSubtitle = styled.p`
    font-size: ${typography.fontSize[400]};
    line-height: 1.8;
    color: ${({ theme }) => theme.subtleText};
    max-width: 600px;
    margin: 0 auto 2rem;
`;

const ExternalLink = styled.a`
    display: inline-flex;
    align-items: center;
    gap: 0.5rem;
    font-size: ${typography.fontSize[300]};
    font-weight: ${typography.fontWeight.semibold};
    color: ${colors.emerald[500]};
    text-decoration: none;
    transition: opacity 0.2s ease;

    &:hover {
        opacity: 0.8;
        color: ${colors.emerald[500]};
    }
`;

const ContentSection = styled.section`
    margin-bottom: 4.5rem;
`;

const SectionTitle = styled(SubsectionHeadline)`
    margin-bottom: 1.5rem;
`;

const Paragraph = styled.p`
    font-size: ${typography.fontSize[300]};
    line-height: 1.8;
    color: ${({ theme }) => theme.subtleText};
    margin: 0 0 1.25rem;
`;

const TechGrid = styled.div`
    display: flex;
    flex-wrap: wrap;
    gap: 0.75rem;
    margin-top: 1rem;
`;

const TechChip = styled.span`
    font-size: ${typography.fontSize[100]};
    font-weight: ${typography.fontWeight.medium};
    color: ${({ theme }) => theme.text};
    background: ${({ theme }) => theme.cardBackground};
    border: 1px solid ${({ theme }) => theme.borderLight};
    border-radius: 999px;
    padding: 0.4rem 1rem;
`;

const ScreenshotFull = styled.img`
    width: 100%;
    border-radius: 12px;
    border: 1px solid ${({ theme }) => theme.borderLight};
    margin: 2.5rem 0;
    box-shadow: 0 4px 20px rgba(0, 0, 0, 0.08);
`;

const ImageGrid = styled.div`
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 1.5rem;
    margin: 2.5rem 0;

    @media (max-width: 640px) {
        grid-template-columns: 1fr;
    }
`;

const ScreenshotGrid = styled.img`
    width: 100%;
    border-radius: 12px;
    border: 1px solid ${({ theme }) => theme.borderLight};
    box-shadow: 0 4px 20px rgba(0, 0, 0, 0.08);
`;

const ImageCaption = styled.span`
    display: block;
    text-align: center;
    font-size: ${typography.fontSize[100]};
    color: ${({ theme }) => theme.subtleText};
    margin-top: 0.75rem;
`;

const CartSense = () => {
    return (
        <PageWrapper>
            <HeroSection>
                <HeroLabel>Founder &amp; Software Engineer &middot; Dec 2025 &ndash; Present</HeroLabel>
                <HeroTitle>CartSense</HeroTitle>
                <HeroSubtitle>
                    An AI-powered meal generator that creates personalized recipes based on
                    your dietary restrictions and preferences — then builds your ingredient
                    list and adds everything to your Kroger cart in one click.
                </HeroSubtitle>
                <ExternalLink
                    href="https://www.cartsenseapp.com"
                    target="_blank"
                    rel="noopener noreferrer"
                >
                    Visit cartsenseapp.com &#8599;
                </ExternalLink>
            </HeroSection>

            <ScreenshotFull src={PromptPage} alt="CartSense — AI meal prompt page" />

            <ContentSection>
                <SectionTitle>Overview</SectionTitle>
                <Paragraph>
                    CartSense is a production web application I built end-to-end as a solo
                    founder and product engineer. From concept through deployment I owned the
                    full stack — Next.js and TypeScript on the front end, Firebase behind it,
                    and the authentication, subscription, onboarding, and meal-generation
                    systems in between.
                </Paragraph>
                <Paragraph>
                    Users set their dietary restrictions and preferences, then describe what
                    they're in the mood for. CartSense generates personalized meals with full
                    recipes, nutritional info, and a smart ingredient list — then connects
                    directly to Kroger's API to add everything to their cart.
                </Paragraph>
            </ContentSection>

            <ImageGrid>
                <div>
                    <ScreenshotGrid src={ViewMeal} alt="CartSense — meal detail with nutrition and AI chat" />
                    <ImageCaption>Meal detail with nutrition & AI modifications</ImageCaption>
                </div>
                <div>
                    <ScreenshotGrid src={IngrediantsList} alt="CartSense — ingredient list with prices" />
                    <ImageCaption>Smart ingredient list with live pricing</ImageCaption>
                </div>
            </ImageGrid>

            <ContentSection>
                <SectionTitle>My Role</SectionTitle>
                <Paragraph>
                    Everything. Product strategy, application architecture, frontend and backend
                    engineering, the design system and component library, and deployment. CartSense
                    is a solo-built product covering the entire lifecycle, not just the
                    design-to-code handoff.
                </Paragraph>
            </ContentSection>

            <ScreenshotFull src={AddToCart} alt="CartSense — recipe steps and add to cart flow" />

            <ImageGrid>
                <div>
                    <ScreenshotGrid src={SavedMeals} alt="CartSense — saved meals library" />
                    <ImageCaption>Saved meals library with search & sharing</ImageCaption>
                </div>
                <div>
                    <ScreenshotGrid src={PromptPage} alt="CartSense — AI prompt with quick suggestions" />
                    <ImageCaption>AI prompt with quick meal suggestions</ImageCaption>
                </div>
            </ImageGrid>

            <ContentSection>
                <SectionTitle>Tech Stack</SectionTitle>
                <Paragraph>
                    Built with a modern frontend and backend stack, optimized for performance
                    and developer experience.
                </Paragraph>
                <TechGrid>
                    <TechChip>React</TechChip>
                    <TechChip>Next.js</TechChip>
                    <TechChip>Node.js</TechChip>
                    <TechChip>Firebase</TechChip>
                    <TechChip>Styled Components</TechChip>
                    <TechChip>Vercel</TechChip>
                </TechGrid>
            </ContentSection>
        </PageWrapper>
    );
};

export default CartSense;
